/**
 * A table of one entity kind, chosen by the URL.
 *
 * One screen serves requirements, parts, ports, interfaces and verification -
 * and whatever the registry gains next - because everything that differs
 * between them is described by /api/schema.
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import EntityActions from '../components/entity/EntityActions'
import EntityTable from '../components/entity/EntityTable'
import EmptyState from '../components/common/EmptyState'
import HelpTip from '../components/common/HelpTip'
import { findKind } from '../schema'
import { useSchema } from '../state/AppData'

export default function EntityList() {
  const { plural = '' } = useParams()
  const schema = useSchema()
  const navigate = useNavigate()
  /**
   * Bumped after an import, to make the table re-read itself.
   *
   * Through the table's `key` rather than a callback it exposes: EntityTable
   * fetches on mount, so a new key is a remount and a fresh page. A refresh
   * prop would be a second way of doing the same thing.
   */
  const [generation, setGeneration] = useState(0)

  const kind = findKind(schema, plural)
  if (!kind) {
    return (
      <EmptyState
        title={`No such thing as '${plural}'`}
        description="BEST does not have an entity kind at that address."
        actions={[{ label: 'Go to the dashboard', onClick: () => navigate('/') }]}
      />
    )
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          {/* The kind's own description is a DEFINITION OF THE NOUN - "a state
              a part can be in", on the page called States - so it is behind the
              '?' rather than under the title. It is kept, not deleted: a couple
              of kinds say something a reader could not have guessed, and the
              rule is one rule for every kind rather than a judgement per kind. */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5">{kind.label}s</Typography>
            {kind.help && (
              <HelpTip title={kind.help} label={`What a ${kind.label.toLowerCase()} is`} />
            )}
          </Box>
        </Box>
        <EntityActions
          kind={kind}
          onCreated={(id) => navigate(`/${kind.plural}/${encodeURIComponent(id)}`)}
          onImported={() => setGeneration((value) => value + 1)}
        />
      </Stack>

      {/* Keyed by kind, so switching from Ports to States REMOUNTS the table
          rather than reusing it. Both are the ':plural' route, so without this
          the rows, the search text and the error all survive a change of kind -
          and the grid draws the previous kind's rows under the new one's
          columns until the debounced fetch returns. */}
      <EntityTable key={`${kind.plural}:${generation}`} kind={kind} />
    </Stack>
  )
}
